import React, { useState, useRef, useEffect, useMemo } from "react";
import { IoSend } from "react-icons/io5";
import genaiicon from "../logos/Kell Benefits icon 4.webp";
import attachIcon from "../logos/Vector.png";
import GaeIcon from "../logos/gaeIcon";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import useStreaming from "../hooks/useStreaming";
import remarkGfm from "remark-gfm";
import FileUploadCard from "../ui-components/FileUploadCard";

import { apiUrl } from "../constants/constants";

interface Message {
  user: string;
  text: string;
  isMarkdown?: boolean;
}

interface ChildComponentProps {
  newChat: boolean;
  updateNewChatValue: () => void;
  messagesOfSelectedThread: any;
  setHistory: React.Dispatch<
    React.SetStateAction<{ [key: string]: Message[] }>
  >;
  threadId: number;
  selectedThread: number | null;
}

const ChildComponent = ({
  newChat,
  updateNewChatValue,
  setHistory,
  threadId,
  messagesOfSelectedThread,
}: ChildComponentProps) => {
  const { messages: streamMessages, startStreaming } = useStreaming();

  console.log("messagesOfSelectedThread", messagesOfSelectedThread);

  // Initialize messages from sessionStorage if exists
  const [messages, setMessages] = useState<Message[]>(() => {
    const stored = sessionStorage.getItem(`chat_messages_${threadId}`);
    return stored ? JSON.parse(stored) : [];
  });

  const [showInitialContent, setShowInitialContent] = useState(() => {
    const stored = sessionStorage.getItem(`show_initial_${threadId}`);
    return stored ? JSON.parse(stored) : newChat;
  });

  // Save to sessionStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(
        `chat_messages_${threadId}`,
        JSON.stringify(messages)
      );
    }
  }, [messages, threadId]);

  // Save initial content state
  useEffect(() => {
    sessionStorage.setItem(
      `show_initial_${threadId}`,
      JSON.stringify(showInitialContent)
    );
  }, [showInitialContent, threadId]);

  // Clear session storage when starting new chat
  useEffect(() => {
    if (newChat) {
      sessionStorage.removeItem(`chat_messages_${threadId}`);
      sessionStorage.removeItem(`show_initial_${threadId}`);
      setMessages([]);
      setShowInitialContent(true);
    }
  }, [newChat, threadId]);

  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Thinking...");
  // const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [currentUpload, setCurrentUpload] = useState<{
    fileName: string;
    progress: number;
  } | null>(null);
  const [dynamicUploadFileUrl, setDynamicUploadFileUrl] = useState(null);

  useMemo(() => {
    if (messagesOfSelectedThread) {
      setMessages(messagesOfSelectedThread);
    }
  }, [messagesOfSelectedThread]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    setShowInitialContent(newChat);
    if (newChat) setMessages([]);
  }, [newChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (value?: string) => {
    if (!input.trim() && !value?.trim()) return;

    setInput("");
    setShowInitialContent(false);
    const question = value ? value : input;

    // Add user message
    setMessages((prev) => [...prev, { user: "You", text: question }]);
    // Add empty AI message that will be updated
    setMessages((prev) => [...prev, { user: "ChatGPT", text: "" }]);

    setLoadingText("Thinking");
    // setTimeout(() => setLoadingText('Fetching data...'), 5000);
    setTimeout(() => setLoadingText("Extracting information"), 5000);

    try {
      setLoading(true);

      // Start streaming with the proper options
      await startStreaming({
        question: question,
        thread_id: threadId.toString(),
        content_url: dynamicUploadFileUrl,
        setHistory,
        threadId,
      });
    } catch (error) {
      console.error("Error:", error);
      setMessages((prevMessages) => {
        const newMessages = [...prevMessages];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.user === "ChatGPT") {
          lastMessage.text =
            "Sorry, there was an error processing your request.";
        }
        return newMessages;
      });
      setLoading(false);
    } finally {
      setLoading(false);
    }
    // setHistory((prev: any) => ({
    //   ...prev,
    //   [threadId]: [
    //     ...(prev[threadId] || []),
    //     { user: "You", text: question },
    //     { user: "ChatGPT", text: streamMessages[streamMessages.length - 1] }
    //   ]
    // }));
    updateNewChatValue();
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) return;

    setCurrentUpload({
      fileName: file.name,
      progress: 0,
    });

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setCurrentUpload((prev) =>
          prev
            ? {
                ...prev,
                progress: Math.min(
                  prev.progress + Math.floor(Math.random() * 14),
                  90
                ),
              }
            : null
        );
      }, 1000);

      const response = await fetch(apiUrl + "/api/dynamic_upload", {
        method: "POST",
        body: formData,
      });

      // console.log('response', response);

      clearInterval(progressInterval);

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      // console.log('data', data);
      setDynamicUploadFileUrl(data?.content_url);

      // Set progress to 100% on success
      setCurrentUpload((prev) =>
        prev
          ? {
              ...prev,
              progress: 100,
            }
          : null
      );

      // setUploadedFileId(data.file_id);
    } catch (error) {
      console.error("Upload error:", error);
      // setCurrentUpload(null);
      // setMessages(prev => [...prev, {
      //   user: 'ChatGPT',
      //   text: 'Sorry, there was an error uploading your file.',
      //   isMarkdown: true
      // }]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (streamMessages.length > 0) {
      setMessages((prevMessages) => {
        const newMessages = [...prevMessages];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.user === "ChatGPT") {
          // Get the latest streaming message
          lastMessage.text = streamMessages[streamMessages.length - 1];
        }

        return newMessages;
      });
    }
  }, [streamMessages]);

  // const data1 = "To determine the best coverage for mental health providers, we need to consider several factors such as copayments, coinsurance, network restrictions, and any specific limitations or exceptions related to mental health services. Below is a comparison of the available health plans based on the provided context:\n\n<table>\n<tr>\n<th>Plan Name</th>\n<th>In-Network Mental Health Coverage</th>\n<th>Out-of-Network Mental Health Coverage</th>\n<th>Limitations & Exceptions</th>\n</tr>\n<tr>\n<td>AFA CPOSII 6250 100 50 IntRX PY V23</td>\n<td>0% coinsurance</td>\n<td>50% coinsurance</td>\n<td>Out-of-network precertification required or $400 penalty applies per occurrence.</td>\n</tr>\n<tr>\n<td>AFA CPOSII 3500 100 50 IntRX PY V23</td>\n<td>0% coinsurance</td>\n<td>50% coinsurance</td>\n<td>Out-of-network precertification required or $400 penalty applies per occurrence.</td>\n</tr>\n<tr>\n<td>AFA CPOSII 4000 70 50 PY V23</td>\n<td>30% coinsurance</td>\n<td>50% coinsurance</td>\n<td>Out-of-network precertification required or $400 penalty applies per occurrence.</td>\n</tr>\n<tr>\n<td>AFA CPOSII 500 100 50 PY V23</td>\n<td>0% coinsurance</td>\n<td>50% coinsurance</td>\n<td>Out-of-network precertification required or $400 penalty applies per occurrence.</td>\n</tr>\n<tr>\n<td>AFA CPOSII 6500 HSA 70 50 E PY V23</td>\n<td>30% coinsurance</td>\n<td>50% coinsurance</td>\n<td>Out-of-network precertification required or $400 penalty applies per occurrence.</td>\n</tr>\n</table>\n\n**Summary:**\n- **AFA CPOSII 6250 100 50 IntRX PY V23** and **AFA CPOSII 500 100 50 PY V23** offer the best in-network coverage with 0% coinsurance for mental health services.\n- All plans require out-of-network precertification to avoid a $400 penalty.\n- Out-of-network coverage is generally at 50% coinsurance across all plans.\n\n**Recommendation:**\nIf you prioritize in-network coverage with minimal out-of-pocket costs, consider the AFA CPOSII 6250 100 50 IntRX PY V23 or AFA CPOSII 500 100 50 PY V23 plans. However, ensure that your preferred mental health providers are within the network to maximize benefits.\n\nIf you have any specific preferences or additional details about your needs, please let me know so I can provide more tailored recommendations."

  return (
    <div className="flex-grow flex flex-col bg-[#f2f4f9] overflow-hidden h-lvh">
      <div className="bg-white my-8 mr-8 rounded-xl flex-grow flex flex-col overflow-hidden">
        {showInitialContent && (
          <div className="p-8 text-center">
            <img
              src={genaiicon}
              alt="Gen AI Enable"
              className="w-[90px] h-[90px] mt-16 mx-auto rounded-full"
            />

            <div className="mb-36 mt-[20px] font-montserrat">
              <h5 className="text-[16px] font-[500] mb-2">
                Kelly The Benefits Guru
              </h5>
              <p className="text-gray-600 text-[13px] max-w-[600px] mx-auto">
                Kelly is a benefits specialist who compares SBCs, helping
                employees choose the best plan with humor and friendliness.
              </p>
            </div>

            <div className="flex gap-8 mx-auto p-0 w-[800px] -mt-10">
              <button
                className="flex-1 p-4 bg-white border border-gray-200 rounded-lg font-montserrat text-[13px] font-medium leading-[16px] text-gray-700 cursor-pointer transition-all duration-200 hover:border-[#0078d4] hover:shadow-md min-w-0 whitespace-normal h-20 flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(
                    "Which plan has the lowest deductible according to the SBC?"
                  );
                }}
              >
                Which plan has the lowest deductible according to the SBC?
              </button>
              <button
                className="flex-1 p-4 bg-white border border-gray-200 rounded-lg font-montserrat text-[13px] font-medium leading-[16px] text-gray-700 cursor-pointer transition-all duration-200 hover:border-[#0078d4] hover:shadow-md min-w-0 whitespace-normal h-20 flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(
                    "Can you compare the co-pays of all health plans."
                  );
                }}
              >
                Can you compare the co-pays of all health plans.
              </button>
              <button
                className="flex-1 p-4 bg-white border border-gray-200 rounded-lg font-montserrat text-[13px] font-medium leading-[16px] text-gray-700 cursor-pointer transition-all duration-200 hover:border-[#0078d4] hover:shadow-md min-w-0 whitespace-normal h-20 flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit("What plans would best suit for family needs.");
                }}
              >
                What plans would best suit for family needs.
              </button>
              <button
                className="flex-1 p-4 bg-white border border-gray-200 rounded-lg font-montserrat text-[13px] font-medium leading-[16px] text-gray-700 cursor-pointer transition-all duration-200 hover:border-[#0078d4] hover:shadow-md min-w-0 whitespace-normal h-20 flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(
                    "Can you show me a table comparing all the plans?"
                  );
                }}
              >
                Can you show me a table comparing all the plans?
              </button>
            </div>
          </div>
        )}

        {!showInitialContent && (
          <div className="flex-grow p- mt-[20px] overflow-y-auto bg-white flex flex-col scrollbar-hide">
            {messages.map((message: any, index: any) => (
              <div
                key={index}
                className={`p-3.5 mr-[20px] rounded-full max-w-[70%] break-words mb-2.5 text-[15px] ${
                  message.user === "You"
                    ? "self-end bg-[#F2F4F9] text-right"
                    : "self-start bg-[#FFFFFF] text-left"
                }`}
              >
                {message.user === "You" ? (
                  message.text
                ) : (
                  <div className="flex items-top">
                    <div className="p-2 mt-[-5px]">
                      <GaeIcon />
                    </div>
                    {message.text ? (
                      <div className="text-[15px]">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          className="prose prose-sm"
                          components={{
                            ol: ({ children }) => (
                              <ol
                                style={{
                                  paddingLeft: "1em",
                                  listStyle: "decimal",
                                  margin: "0.3em 0",
                                }}
                              >
                                {children}
                              </ol>
                            ),
                            ul: ({ children }) => (
                              <ul
                                style={{
                                  paddingLeft: "1em",
                                  listStyle: "disc",
                                  margin: "0.3em 0",
                                }}
                              >
                                {children}
                              </ul>
                            ),
                            li: ({ children }) => (
                              <li style={{ margin: "0.1em 0" }}>{children}</li>
                            ),
                            table: ({ children }) => (
                              <table className="table text-[15px]">
                                {children}
                              </table>
                            ),
                            p: ({ children }) => (
                              <p style={{ margin: "0.3em 0" }}>{children}</p>
                            ),
                          }}
                        >
                          {message.text}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-end gap-1 animate-pulse text-gray-500 italic p-2">
                          {loadingText}
                          <div className="flex gap-1 mb-1">
                            <div className="h-1 w-1 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                            <div className="h-1 w-1 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                            <div className="h-1 w-1 bg-gray-500 rounded-full animate-bounce"></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
        <div className="p-5 sticky bottom-5 mb bg-white">
          <div className="bg-[#f2f4f9] rounded-[24px] p-3 px-5 mx-auto max-w-[800px]">
            {currentUpload && (
              <FileUploadCard
                fileName={currentUpload.fileName}
                progress={currentUpload.progress}
                onClose={() => {
                  setCurrentUpload(null);
                  setDynamicUploadFileUrl(null);
                }}
              />
            )}
            <form
              onSubmit={(e: any) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                className="w-full border-none outline-none p-2 text-sm bg-transparent font-montserrat font-[500] placeholder-gray-400"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type Anything..."
              />
              <button
                type="submit"
                className="bg-transparent border-none p-2 cursor-pointer text-gray-600 hover:text-[#0078d4] flex items-center"
                disabled={loading}
              >
                <IoSend size={20} />
              </button>
            </form>
            <div className="flex justify-between gap-3">
              <button
                className="bg-transparent border-none p-2 cursor-pointer text-gray-600 hover:text-[#0078d4] flex items-center"
                onClick={() => fileInputRef.current?.click()}
                // disabled={true}
              >
                <img src={attachIcon} alt="Attach" className="w-5 h-5" />
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={(e: any) => handleFileUpload(e)}
                accept=".pdf,.doc,.docx"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-center mb-30">
          <h5 className="text-[14px] font-[350] mb-2 text-[#5D5F62]">
            Kelly can make mistakes. Please check important info.
          </h5>
        </div>
      </div>
    </div>
  );
};
export default ChildComponent;
